import {useEffect,useMemo,useRef,useState} from 'react'
import './arabicCourseAudio.css'
import {audioUrl,deleteCourseAudio,listCourseAudio,setCourseAudio,uploadCourseAudio,unitLabel,type CourseAudio} from './arabicCourse'

// Arabic Lab → My course → Course audio. The learner's own copy of their course audio (e.g. downloaded from the
// textbook's companion site), stored privately on their account and played only to them. The player is built for
// shadowing: slower speeds, a quick step back, and an A–B loop around a phrase.
const SPEEDS=[.6,.75,.9,1]
const UNIT_KEY='dawwin-arabic-course-audio-unit'
type Upload={done:number;total:number;failed:string[]}

export default function CourseAudioPanel({notebookId,courseName,collection,onCollection}:{notebookId:string;courseName:string;collection:string;onCollection:(c:string)=>void}){
 const [audio,setAudio]=useState<CourseAudio[]|null>(null)
 const [error,setError]=useState('')
 const [unit,setUnit]=useState<number|null>(()=>{try{const v=localStorage.getItem(`${UNIT_KEY}:${notebookId}`);return v===null?null:Number(v)}catch{return null}})
 const [playing,setPlaying]=useState<string>('')
 const [upload,setUpload]=useState<Upload|null>(null)
 const [newName,setNewName]=useState('')
 const [manage,setManage]=useState(false)

 async function load(){try{setAudio((await listCourseAudio()).audio);setError('')}catch(e:any){setError(e?.message==='http_401'?'Sign in to use your course audio.':'Could not load your course audio.')}}
 useEffect(()=>{void load()},[])

 const collections=useMemo(()=>[...new Set((audio||[]).map(a=>a.collection))],[audio])
 const tracks=useMemo(()=>(audio||[]).filter(a=>a.collection===collection),[audio,collection])
 const units=useMemo(()=>[...new Set(tracks.map(t=>t.unit))].sort((a,b)=>a-b),[tracks])
 const shownUnit=unit!==null&&units.includes(unit)?unit:units[0]??null
 const unitTracks=tracks.filter(t=>t.unit===shownUnit)
 const current=tracks.find(t=>t.id===playing)

 function chooseUnit(u:number){setUnit(u);setPlaying('');try{localStorage.setItem(`${UNIT_KEY}:${notebookId}`,String(u))}catch{/* storage may be unavailable */}}
 async function link(c:string){try{await setCourseAudio(notebookId,c);onCollection(c)}catch{setError('Could not link this audio to the course.')}}

 async function addFiles(files:FileList|null,target:string){
  const list=[...(files||[])].filter(f=>/\.(mp3|m4a)$/i.test(f.name))
  if(!list.length||!target.trim())return
  setUpload({done:0,total:list.length,failed:[]})
  const failed:string[]=[]
  for(const f of list){
   try{await uploadCourseAudio(f,target.trim())}catch{failed.push(f.name)}
   setUpload(u=>u&&{...u,done:u.done+1})
  }
  setUpload({done:list.length,total:list.length,failed})
  await load()
  if(target.trim()!==collection)await link(target.trim())
 }
 async function remove(t:CourseAudio){
  if(!window.confirm(`Delete “${t.title}” from your course audio? You can add it again from your own copy.`))return
  try{await deleteCourseAudio(t.id);if(playing===t.id)setPlaying('');await load()}catch{setError('Could not delete that track.')}
 }

 if(audio===null)return <section className="course-audio" aria-label="Course audio"><p className="arabic-course-muted">{error||'Loading your course audio…'}</p></section>
 return <section className="course-audio" aria-label="Course audio">
  <div className="course-audio-head">
   <div><span className="arabic-lab-eyebrow">COURSE AUDIO · PRIVATE TO YOU</span>
    <p>{collection?`${collection} · ${tracks.length} track${tracks.length===1?'':'s'} in ${units.length} unit${units.length===1?'':'s'}`:collections.length?'Choose which of your audio goes with this course.':'Add the audio that comes with your course book. It is stored on your account and played only to you.'}</p></div>
   <div className="course-audio-head-actions">
    {collections.length>0&&<label><span>AUDIO FOR {courseName.toUpperCase()||'THIS COURSE'}</span><select value={collection} onChange={e=>void link(e.target.value)}><option value="">None</option>{collections.map(c=><option key={c} value={c}>{c}</option>)}</select></label>}
    <button type="button" aria-expanded={manage} onClick={()=>setManage(m=>!m)}>{manage?'Done':'Add or manage audio'}</button>
   </div>
  </div>
  {error&&<p className="arabic-course-error" role="alert">{error}</p>}

  {manage&&<div className="course-audio-manage">
   <p className="arabic-course-muted">Choose the MP3 files from your own download (unzip it first; you can select a whole unit or all of them at once). Tracks named like “Unit 03 - Track 05” are filed under their unit automatically, and adding a track again replaces it.</p>
   <div className="course-audio-add">
    {collection&&<label className="course-audio-file">Add to {collection}<input type="file" accept=".mp3,.m4a,audio/mpeg,audio/mp4" multiple onChange={e=>{void addFiles(e.target.files,collection);e.target.value=''}}/></label>}
    <label><span>NEW AUDIO COLLECTION</span><input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Mastering Arabic 1" aria-label="New audio collection name"/></label>
    <label className={`course-audio-file${newName.trim()?'':' disabled'}`}>Choose files<input type="file" accept=".mp3,.m4a,audio/mpeg,audio/mp4" multiple disabled={!newName.trim()} onChange={e=>{void addFiles(e.target.files,newName);e.target.value=''}}/></label>
   </div>
   {upload&&<p role="status" className="arabic-course-muted">{upload.done<upload.total?`Uploading track ${upload.done+1} of ${upload.total}…`:`Added ${upload.total-upload.failed.length} of ${upload.total} tracks.`}{upload.failed.length>0&&upload.done===upload.total&&<> Could not add: {upload.failed.join(', ')}. Tracks need a unit and track number in the name, e.g. “Unit 03 - Track 05.mp3”.</>}</p>}
   {collection&&unitTracks.length>0&&<ul className="course-audio-manage-list">{unitTracks.map(t=><li key={t.id}><span>{t.title}</span><span className="arabic-course-muted">{(t.byte_size/1048576).toFixed(1)} MB</span><button type="button" onClick={()=>void remove(t)}>Delete</button></li>)}</ul>}
  </div>}

  {collection&&units.length>0&&<>
   <div className="course-audio-units" role="tablist" aria-label="Units">{units.map(u=><button type="button" role="tab" key={u} aria-selected={u===shownUnit} className={u===shownUnit?'selected':''} onClick={()=>chooseUnit(u)}>{unitLabel(u)}</button>)}</div>
   <ol className="course-audio-tracks">{unitTracks.map(t=><li key={t.id}><button type="button" aria-pressed={t.id===playing} className={t.id===playing?'selected':''} onClick={()=>setPlaying(t.id)}>▶ Track {String(t.track).padStart(2,'0')}</button></li>)}</ol>
   {current&&<ShadowPlayer key={current.id} track={current}/>}
  </>}
 </section>
}

/** An audio element with shadowing controls: speed, step back, and an A–B loop that repeats a phrase. */
function ShadowPlayer({track}:{track:CourseAudio}){
 const ref=useRef<HTMLAudioElement>(null)
 const [speed,setSpeed]=useState(1)
 const [a,setA]=useState<number|null>(null)
 const [b,setB]=useState<number|null>(null)
 const [failed,setFailed]=useState(false)
 useEffect(()=>{const el=ref.current;if(el)el.playbackRate=speed},[speed])
 useEffect(()=>{
  const el=ref.current
  if(!el||a===null||b===null)return
  const loop=()=>{if(el.currentTime>=b){el.currentTime=a;void el.play().catch(()=>{})}}
  el.addEventListener('timeupdate',loop)
  return()=>el.removeEventListener('timeupdate',loop)
 },[a,b])
 const now=()=>ref.current?.currentTime??0
 const fmt=(s:number)=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`
 function back(){const el=ref.current;if(el)el.currentTime=Math.max(0,el.currentTime-3)}
 function setEnd(){const t=now();if(a!==null&&t>a){setB(t);const el=ref.current;if(el){el.currentTime=a;void el.play().catch(()=>{})}}}
 return <div className="course-audio-player">
  <p className="course-audio-title">{track.title}</p>
  <audio ref={ref} controls preload="metadata" src={audioUrl(track.id)} onLoadedMetadata={()=>{if(ref.current)ref.current.playbackRate=speed}} onError={()=>setFailed(true)}/>
  {failed&&<p className="arabic-course-error" role="alert">This track could not be played. Try again, or add it again from your own copy.</p>}
  <div className="course-audio-controls">
   <div className="arabic-lab-buttons" role="group" aria-label="Speed">{SPEEDS.map(s=><button type="button" key={s} aria-pressed={speed===s} className={speed===s?'selected':''} onClick={()=>setSpeed(s)}>{s===1?'1×':`${s}×`}</button>)}</div>
   <button type="button" onClick={back}>↺ 3 s</button>
   <div className="course-audio-loop" role="group" aria-label="Repeat a phrase">
    <button type="button" aria-pressed={a!==null} className={a!==null?'selected':''} onClick={()=>{setA(now());setB(null)}}>{a===null?'Set A':`A ${fmt(a)}`}</button>
    <button type="button" disabled={a===null} aria-pressed={b!==null} className={b!==null?'selected':''} onClick={setEnd}>{b===null?'Set B':`B ${fmt(b)}`}</button>
    {(a!==null||b!==null)&&<button type="button" onClick={()=>{setA(null);setB(null)}}>Clear loop</button>}
   </div>
  </div>
  <p className="arabic-course-muted">{a!==null&&b!==null?'Repeating A to B: listen, then say it with the recording.':'Play, tap Set A where a phrase starts and Set B where it ends to repeat it.'}</p>
 </div>
}
